import { Directive, HostListener, OnInit, DestroyRef, inject, output, input } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject, throttleTime } from "rxjs";

/**
 * Notes
 * -----
 * Enhance directive by throttling the click events.
 * Eliminates duplicate events within a certain throttle time.
 *
 * throttleTime() will process first click and ignore the rest.
 *
 * Standard Implementation
 * -----------------------
 *  Anywhere a (click) event is used, it can be replaced with the following:
 *
 *  >- <button clickStop (debounceClick)="myMethodCall()">...</button>
 *
 * Changing Debounce Time
 * ----------------------
 * The default debounce time is 750.
 * To change the debounce time do the following:
 *
 * >- <button clickStop (debounceClick)="myMethodCall() [debounceTime]="1000">...</button>
 *
 * The ClickStopDirective prevents propagation of the same event from being called.
 * @example : 2 (click) events on a card
 *
 */
@Directive({
  selector: "[appClickStop]"
})
export class ClickStopDirective implements OnInit {
  debounceTime = input(750);
  debounceClick = output<any>();

  private clicks = new Subject();
  private _destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.clicks.pipe(throttleTime(this.debounceTime()), takeUntilDestroyed(this._destroyRef)).subscribe((e) => {
      this.debounceClick.emit(e);
    });
  }

  @HostListener("click", ["$event"])
  clickEvent(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.clicks.next(event);
  }
}
